// Central service catalog for the /services page. Each slug matches the image
// under /public/services/{slug}.jpg and is preserved through the booking flow
// via localStorage("voor_service_type") so the reservation draft can be tagged
// with the service the customer chose.
export const SERVICES = [
  {
    slug: "airport-transfers",
    label: "Airport Transfers",
    description: "Meet-and-greet, flight tracking, and door-to-door precision for arrivals and departures.",
    features: ["Flight tracking", "60 min complimentary wait", "Luggage assistance"],
  },
  {
    slug: "hourly-chauffeur",
    label: "Hourly Chauffeur",
    description: "A dedicated chauffeur and vehicle for as long as your day requires.",
    features: ["Flexible multi-stop", "Chauffeur on standby", "Discreet service"],
  },
  {
    slug: "point-to-point",
    label: "Point-to-Point",
    description: "Straight-line executive transfer between any two addresses.",
    features: ["Fixed flat rate", "Direct routing", "On-time guarantee"],
  },
  {
    slug: "corporate-transportation",
    label: "Corporate Transportation",
    description: "Ongoing chauffeur solutions for teams, board meetings, and executive rosters.",
    features: ["Centralized billing", "Preferred driver pairing", "Dedicated account manager"],
  },
  {
    slug: "executive-travel",
    label: "Executive Travel",
    description: "Flagship-class chauffeur service for C-suite and dignitary transfers.",
    features: ["Ultra-luxury fleet", "Advance route surveying", "Private itinerary"],
  },
  {
    slug: "business-meetings",
    label: "Business Meetings",
    description: "Between offices, boardrooms, and negotiations — arrive composed.",
    features: ["Multi-stop routing", "Wi-Fi equipped", "Refreshments on request"],
  },
  {
    slug: "hotel-transportation",
    label: "Hotel Transportation",
    description: "Curbside pickup from any hotel property, day or night.",
    features: ["Concierge coordination", "24/7 dispatch", "Bell-staff handoff"],
  },
  {
    slug: "cruise-port-transfers",
    label: "Cruise Port Transfers",
    description: "Timed arrivals and departures for cruise embarkation and disembarkation.",
    features: ["Port authority coordination", "Group luggage handling", "Meet-at-dock service"],
  },
  {
    slug: "wedding-chauffeur",
    label: "Wedding Chauffeur",
    description: "White-glove ground service for your ceremony, reception, and getaway.",
    features: ["Same-day multi-leg", "Bride & groom vehicle", "Silent gliding arrival"],
  },
  {
    slug: "prom-chauffeur",
    label: "Prom Chauffeur",
    description: "A memorable, professional, and safe evening for the graduating class.",
    features: ["Parent-approved chauffeurs", "Group configurations", "Photo-stop schedule"],
  },
  {
    slug: "birthday-events",
    label: "Birthday Events",
    description: "Turn the arrival into part of the celebration.",
    features: ["Champagne service optional", "Custom playlist", "Multi-venue evenings"],
  },
  {
    slug: "concert-transportation",
    label: "Concert Transportation",
    description: "Skip parking, arrive at the door, glide home when the encore ends.",
    features: ["Venue-side pickup", "Late-return dispatch", "Group vehicles available"],
  },
  {
    slug: "sporting-events",
    label: "Sporting Events",
    description: "Stadium and arena drop-offs with priority routing.",
    features: ["Traffic pattern expertise", "Tailgate coordination", "Post-game recovery"],
  },
  {
    slug: "wine-tours",
    label: "Wine Tours",
    description: "A curated day through the vineyards with no keys to worry about.",
    features: ["Custom winery itinerary", "Full-day rate", "Cellar pickup assistance"],
  },
  {
    slug: "private-city-tours",
    label: "Private City Tours",
    description: "Discreet chauffeur-led exploration of the city, on your terms.",
    features: ["Curated landmark routes", "Local expert chauffeur", "Photo stops"],
  },
  {
    slug: "shopping-trips",
    label: "Shopping Trips",
    description: "Boutique to boutique, with someone to load the trunk.",
    features: ["Multi-store routing", "Package handling", "In-store wait"],
  },
  {
    slug: "medical-transportation",
    label: "Medical Transportation",
    description: "Discreet, comfortable transfers for medical appointments and procedures.",
    features: ["Wheelchair-friendly options", "Patient-first pace", "Family coordination"],
  },
  {
    slug: "long-distance-trips",
    label: "Long Distance Trips",
    description: "Intercity and cross-region journeys, without the fatigue.",
    features: ["Rested chauffeur rotation", "Comfort stops", "Fixed long-haul quote"],
  },
  {
    slug: "vip-security-transportation",
    label: "VIP Security Transportation",
    description: "Coordinated ground movement for protected principals and high-profile clients.",
    features: ["Secure driver credentialing", "Advance route recon", "Discreet vehicle profile"],
  },
  {
    slug: "custom-transportation",
    label: "Custom Transportation",
    description: "Anything not on this list — we build the itinerary around your needs.",
    features: ["Concierge planning", "Multi-day options", "Bespoke vehicle pairing"],
  },
];

export const SERVICE_MAP = SERVICES.reduce((acc, s) => {
  acc[s.slug] = s;
  return acc;
}, {});

// Persist the customer's chosen service across the reservation flow.
const KEY = "voor_service_type";
export const setActiveService = (slug) => {
  try {
    if (slug) localStorage.setItem(KEY, slug);
    else localStorage.removeItem(KEY);
  } catch {
    /* localStorage unavailable */
  }
};
export const getActiveService = () => {
  try {
    return localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
};
export const clearActiveService = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
};
